// Local storage utilities for persisting game data

const STORAGE_KEYS = {
  HIGH_SCORE: 'tetris_biblical_high_score',
  COLLECTED_VERSES: 'tetris_biblical_verses',
  BOOK_PROGRESS: 'tetris_biblical_progress',
  STATISTICS: 'tetris_biblical_stats',
  GAME_STATE: 'tetris_biblical_game_state',
};

// High Score
export const getHighScore = () => {
  try {
    const score = localStorage.getItem(STORAGE_KEYS.HIGH_SCORE);
    return score ? parseInt(score, 10) : 0;
  } catch (error) {
    console.error('Error reading high score:', error);
    return 0;
  }
};

export const setHighScore = (score, force = false) => {
  try {
    const currentHigh = getHighScore();
    if (force || score > currentHigh) {
      localStorage.setItem(STORAGE_KEYS.HIGH_SCORE, score.toString());
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error saving high score:', error);
    return false;
  }
};

// Collected Verses
export const getCollectedVerses = () => {
  try {
    const verses = localStorage.getItem(STORAGE_KEYS.COLLECTED_VERSES);
    return verses ? JSON.parse(verses) : [];
  } catch (error) {
    console.error('Error reading collected verses:', error);
    return [];
  }
};

export const addCollectedVerse = (verse) => {
  try {
    const verses = getCollectedVerses();
    // Avoid duplicates
    const exists = verses.some(v => v.reference === verse.reference);
    if (!exists) {
      verses.push({
        ...verse,
        collectedAt: new Date().toISOString(),
      });
      localStorage.setItem(STORAGE_KEYS.COLLECTED_VERSES, JSON.stringify(verses));
    }
    return verses;
  } catch (error) {
    console.error('Error adding collected verse:', error);
    return [];
  }
};

export const removeCollectedVersesByBook = (bookId) => {
  try {
    const verses = getCollectedVerses();
    const updatedVerses = verses.filter(v => v.bookId !== bookId);
    localStorage.setItem(STORAGE_KEYS.COLLECTED_VERSES, JSON.stringify(updatedVerses));
    return updatedVerses;
  } catch (error) {
    console.error('Error removing collected verses by book:', error);
    return [];
  }
};

export const removeCollectedVersesByChapter = (bookId, chapter) => {
  try {
    const verses = getCollectedVerses();
    const updatedVerses = verses.filter(v => !(v.bookId === bookId && v.chapter === chapter));
    localStorage.setItem(STORAGE_KEYS.COLLECTED_VERSES, JSON.stringify(updatedVerses));
    return updatedVerses;
  } catch (error) {
    console.error('Error removing collected verses by chapter:', error);
    return [];
  }
};

export const clearCollectedVerses = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.COLLECTED_VERSES);
  } catch (error) {
    console.error('Error clearing verses:', error);
  }
};

// Book Progress - Enhanced with chapter tracking
export const getBookProgress = (bookId) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEYS.BOOK_PROGRESS);
    const allProgress = progress ? JSON.parse(progress) : {};
    return allProgress[bookId] || {
      verseIndex: 0,
      percentage: 0,
      currentChapter: 1,
      currentVerseInChapter: 0,
      chaptersCompleted: 0,
      totalVersesRevealed: 0,
      score: 0,
      lines: 0,
      level: 1,
      lastPlayed: null,
      isStarted: false
    };
  } catch (error) {
    console.error('Error reading book progress:', error);
    return {
      verseIndex: 0,
      percentage: 0,
      currentChapter: 1,
      currentVerseInChapter: 0,
      chaptersCompleted: 0,
      totalVersesRevealed: 0,
      score: 0,
      lines: 0,
      level: 1,
      lastPlayed: null,
      isStarted: false
    };
  }
};

export const updateBookProgress = (bookId, progressData) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEYS.BOOK_PROGRESS);
    const allProgress = progress ? JSON.parse(progress) : {};

    const currentProgress = allProgress[bookId] || {};

    allProgress[bookId] = {
      ...currentProgress,
      ...progressData,
      lastPlayed: new Date().toISOString(),
      isStarted: true
    };

    localStorage.setItem(STORAGE_KEYS.BOOK_PROGRESS, JSON.stringify(allProgress));
    return allProgress[bookId];
  } catch (error) {
    console.error('Error updating book progress:', error);
    return null;
  }
};

// Get all books progress
export const getAllBooksProgress = () => {
  try {
    const progress = localStorage.getItem(STORAGE_KEYS.BOOK_PROGRESS);
    return progress ? JSON.parse(progress) : {};
  } catch (error) {
    console.error('Error reading all books progress:', error);
    return {};
  }
};

// Save complete game state for resuming
export const saveGameState = (bookId, gameState) => {
  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEYS.GAME_STATE) || '{}');
    allStates[bookId] = {
      ...gameState,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(allStates));
    return true;
  } catch (error) {
    console.error('Error saving game state:', error);
    return false;
  }
};

// Load game state for resuming
export const loadGameState = (bookId) => {
  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEYS.GAME_STATE) || '{}');
    return allStates[bookId] || null;
  } catch (error) {
    console.error('Error loading game state:', error);
    return null;
  }
};

// Clear game state (when starting fresh)
export const clearGameState = (bookId) => {
  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEYS.GAME_STATE) || '{}');
    delete allStates[bookId];
    localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(allStates));
    return true;
  } catch (error) {
    console.error('Error clearing game state:', error);
    return false;
  }
};

// Statistics
export const getStatistics = () => {
  try {
    const stats = localStorage.getItem(STORAGE_KEYS.STATISTICS);
    return stats ? JSON.parse(stats) : {
      gamesPlayed: 0,
      totalScore: 0,
      totalLines: 0,
      totalVersesRevealed: 0,
      booksCompleted: [],
    };
  } catch (error) {
    console.error('Error reading statistics:', error);
    return {
      gamesPlayed: 0,
      totalScore: 0,
      totalLines: 0,
      totalVersesRevealed: 0,
      booksCompleted: [],
    };
  }
};

export const updateStatistics = (updates) => {
  try {
    const currentStats = getStatistics();
    const newStats = {
      gamesPlayed: currentStats.gamesPlayed + (updates.gamesPlayed || 0),
      totalScore: currentStats.totalScore + (updates.totalScore || 0),
      totalLines: currentStats.totalLines + (updates.totalLines || 0),
      totalVersesRevealed: currentStats.totalVersesRevealed + (updates.totalVersesRevealed || 0),
      booksCompleted: updates.booksCompleted || currentStats.booksCompleted,
    };
    localStorage.setItem(STORAGE_KEYS.STATISTICS, JSON.stringify(newStats));
    return newStats;
  } catch (error) {
    console.error('Error updating statistics:', error);
    return getStatistics();
  }
};

export const setStatistics = (stats) => {
  try {
    localStorage.setItem(STORAGE_KEYS.STATISTICS, JSON.stringify(stats));
    return stats;
  } catch (error) {
    console.error('Error setting statistics:', error);
    return stats;
  }
};

// Mark book as completed
export const markBookAsCompleted = (bookId) => {
  try {
    const stats = getStatistics();
    if (!stats.booksCompleted.includes(bookId)) {
      stats.booksCompleted.push(bookId);
      localStorage.setItem(STORAGE_KEYS.STATISTICS, JSON.stringify(stats));
      return true; // Book was newly completed
    }
    return false; // Book was already completed
  } catch (error) {
    console.error('Error marking book as completed:', error);
    return false;
  }
};

// Get completed books count
export const getCompletedBooksCount = () => {
  try {
    const stats = getStatistics();
    return stats.booksCompleted ? stats.booksCompleted.length : 0;
  } catch (error) {
    console.error('Error getting completed books count:', error);
    return 0;
  }
};

// Get total chapters completed across all books
export const getTotalChaptersCompleted = () => {
  try {
    const allProgress = getAllBooksProgress();
    return Object.values(allProgress).reduce((total, book) => {
      return total + (book.chaptersCompleted || 0);
    }, 0);
  } catch (error) {
    console.error('Error getting total chapters completed:', error);
    return 0;
  }
};

// Clear all game data from localStorage (for new users or logout)
export const clearAllGameData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.HIGH_SCORE);
    localStorage.removeItem(STORAGE_KEYS.COLLECTED_VERSES);
    localStorage.removeItem(STORAGE_KEYS.BOOK_PROGRESS);
    localStorage.removeItem(STORAGE_KEYS.STATISTICS);
    localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
    console.log('✅ All game data cleared from localStorage');
    return true;
  } catch (error) {
    console.error('Error clearing all game data:', error);
    return false;
  }
};

// Initialize fresh user data (called on new user registration)
export const initializeFreshUserData = () => {
  try {
    clearAllGameData();

    // Set initial empty statistics
    const initialStats = {
      gamesPlayed: 0,
      totalScore: 0,
      totalLines: 0,
      totalVersesRevealed: 0,
      booksCompleted: [],
    };
    localStorage.setItem(STORAGE_KEYS.STATISTICS, JSON.stringify(initialStats));
    localStorage.setItem(STORAGE_KEYS.HIGH_SCORE, '0');

    console.log('✅ Fresh user data initialized');
    return true;
  } catch (error) {
    console.error('Error initializing fresh user data:', error);
    return false;
  }
};